import {
  Button,
  CircularProgress,
  Popover,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Stack,
} from '@mui/material';
import { Product } from '../../store/config/types';
import { MouseEvent, useEffect, useState } from 'react';
import { Warehouse } from '../../store/config/types/warehouses.types';

export interface EditLotLinkProps {
  currentLotId?: string | null;
  warehouseId?: number;
  warehouses?: Warehouse[];
  product?: Product;
  dialogTitle?: string;
  disabled?: boolean;
  selectableWarehoue?: boolean;
  // eslint-disable-next-line
  onSubmit?: (newLotId: string, warehouseId?: number) => void;
}
export default function EditLotIdLink({
  currentLotId,
  warehouseId,
  warehouses,
  selectableWarehoue,
  onSubmit,
  dialogTitle,
  product,
  disabled,
}: EditLotLinkProps) {
  const [newValue, setNewValue] = useState(currentLotId);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(warehouseId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    setNewValue(currentLotId);
  }, [currentLotId]);

  useEffect(() => {
    if (!anchorEl) {
      setNewValue(currentLotId);
      setSelectedWarehouseId(warehouseId);
    }
  }, [anchorEl]);

  const handleChangeLot = () => {
    if (onSubmit && newValue) {
      onSubmit(newValue, selectedWarehouseId);
    }
    handleClose();
  };

  function handleChangeWarehouse(event: SelectChangeEvent<number>) {
    setSelectedWarehouseId(+event.target.value);
  }

  function handleOpenPopover(event: MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  const openPopover = Boolean(anchorEl);

  function getWarehouseName(selectedWarehouseId: number | undefined) {
    return warehouses?.find((w) => w.warehouseId === selectedWarehouseId)?.name ?? 'No warehouse selected';
  }

  return (
    <>
      <Stack>
        <Link onClick={disabled ? undefined : handleOpenPopover} sx={{ cursor: 'pointer' }}>
          <Typography variant="subtitle1" color={!currentLotId ? 'gray' : disabled ? 'black' : undefined}>
            {currentLotId ?? 'Enter an ID'}
          </Typography>
        </Link>
        <Typography variant="caption" sx={{ color: 'gray' }}>
          {getWarehouseName(selectedWarehouseId)}
        </Typography>
      </Stack>

      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <DialogTitle>
          <Typography variant="subtitle2">{dialogTitle ?? `Change Lot`} </Typography>
          <Typography variant="h5">{product?.name}</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container pt={2} flexDirection={'column'} gap={1}>
            <Grid>
              <TextField
                fullWidth
                variant="outlined"
                label="Default Lot"
                name="lot"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                disabled={disabled}
              />
            </Grid>
            {selectableWarehoue && warehouses?.length && (
              <Grid>
                <FormControl fullWidth>
                  <InputLabel id="warehouse-select-label">Warehouse</InputLabel>
                  <Select
                    labelId="warehouse-select-label"
                    value={selectedWarehouseId}
                    label="Warehouse"
                    onChange={handleChangeWarehouse}
                  >
                    {warehouses?.map((warehouse) => (
                      <MenuItem key={warehouse.warehouseId} value={warehouse.warehouseId}>
                        {warehouse.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item flexGrow={1} pl={2}>
              {disabled && <CircularProgress size={32} />}
            </Grid>
            <Grid item>
              <Button variant="text" color="error" onClick={handleClose} disabled={disabled}>
                Cancel
              </Button>
              <Button variant="contained" color="success" disabled={disabled || !newValue} onClick={handleChangeLot}>
                Change
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Popover>
    </>
  );
}
